import { create } from 'zustand'
import { v4 as uuidv4 } from 'uuid'
import type { AppData, Project } from '../types/data'
import { getUserData, saveUserData, getProject, pushProject, deleteProject as deleteProjectApi } from '../services/api'
import { 
  getOrCreateUserUuid, 
  switchUser
} from '../services/userManager'
import {
  getProjectsFromUserDB,
  getAllItemsFromUserDB,
  saveProjectToUserDB,
  saveItemsToUserDB,
  deleteProjectFromUserDB,
  updateProjectOpened
} from '../services/dbManager'
import {
  pushProjectToServer as pushProjectWithMedia,
  pushAllProjectsToServer as pushAllProjectsWithMedia
} from '../services/storage/index'

interface DataState {
  data: AppData
  userUuid: string | null
  // 认证相关
  authToken: string | null
  refreshToken: string | null
  setAuthToken: (token: string | null, refreshToken: string | null) => void
  // 数据操作
  initData: () => Promise<void>
  setUserUuid: (uuid: string) => Promise<void>
  setCurrentProject: (projectId: string | null) => void
  addProject: (project: Project) => void
  updateProject: (projectId: string, updates: Partial<Project>, syncToServer?: boolean) => void
  deleteProject: (projectId: string) => void
  saveData: (syncToServer?: boolean) => Promise<void>
  getCurrentProject: () => Project | null
  pullProjectFromServer: (projectId: string) => Promise<void>
  pushProjectToServer: (projectId: string) => Promise<void>
  clearProjectData: (projectId: string, clearServer?: boolean) => Promise<void>
  // 批量操作
  pushAllProjectsToServer: (onProgress?: (current: number, total: number, projectName: string) => void) => Promise<{ success: number; failed: number }>
  pullAllProjectsFromServer: (onProgress?: (current: number, total: number, projectName: string) => void) => Promise<{ success: number; failed: number }>
}

const defaultData: AppData = {
  projects: [],
  currentProjectId: null,
  userUuid: null
}

// 从 localStorage 恢复 token
const getStoredTokens = () => {
  try {
    const authToken = localStorage.getItem('authToken')
    const refreshToken = localStorage.getItem('refreshToken')
    return { authToken, refreshToken }
  } catch {
    return { authToken: null, refreshToken: null }
  }
}

const storedTokens = getStoredTokens()

export const useDataStore = create<DataState>((set, get) => ({
  data: defaultData,
  userUuid: null,
  authToken: storedTokens.authToken,
  refreshToken: storedTokens.refreshToken,

  setAuthToken: (token: string | null, refreshToken: string | null) => {
    // 保存到 localStorage
    try {
      if (token) {
        localStorage.setItem('authToken', token)
      } else {
        localStorage.removeItem('authToken')
      }
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
      } else {
        localStorage.removeItem('refreshToken')
      }
    } catch (e) {
      console.warn('保存 token 到 localStorage 失败:', e)
    }
    
    set({ authToken: token, refreshToken })
  },

  initData: async () => {
    try {
      // 使用用户管理服务获取或创建userUuid（会自动保存到用户列表）
      const userUuid = await getOrCreateUserUuid()
      set({ userUuid })

      // 从服务器同步用户信息（昵称等）
      try {
        const { syncUserInfoFromServer } = await import('../services/userManager')
        await syncUserInfoFromServer(userUuid)
      } catch (syncError) {
        console.warn('[initData] 同步用户信息失败（不影响使用）:', syncError)
      }

      // 从用户独立数据库 IN_{uuid} 加载数据
      let loadedData: AppData | null = null
      try {
        // 加载所有项目
        const allProjects = await getProjectsFromUserDB(userUuid)

        // 加载所有 items
        const allItems = await getAllItemsFromUserDB(userUuid)

        // 将 items 分配到对应的项目
        const projectsWithItems = allProjects.map(project => ({
          ...project,
          items: allItems.filter(item => item.projectId === project.id || item.projectId === project.uuid) || [],
          groups: project.groups || []
        }))

        if (projectsWithItems.length > 0) {
          // 找到当前打开的项目（opened=true）或第一个项目
          const openedProject = projectsWithItems.find(p => p.opened)
          const currentProjectId = openedProject?.id || projectsWithItems[0].id

          loadedData = {
            projects: projectsWithItems,
            currentProjectId,
            userUuid: userUuid
          }
          console.log('[initData] 从用户数据库加载了', projectsWithItems.length, '个项目，共', 
            projectsWithItems.reduce((sum, p) => sum + (p.items?.length || 0), 0), '个 items（用户:', userUuid, '）')
        }
      } catch (dbError) {
        console.warn('[initData] 从用户数据库加载失败:', dbError)
      }

      if (loadedData && loadedData.projects.length > 0) {
        set({ data: loadedData })
        return
      }

      // 如果用户数据库没有数据，尝试从服务器加载
      const serverData = await getUserData(userUuid)
      if (serverData && serverData.projects && serverData.projects.length > 0) {
        console.log('[initData] 从服务器加载了', serverData.projects.length, '个项目（用户:', userUuid, '）')
        
        // 保存到用户数据库
        for (const project of serverData.projects) {
          await saveProjectToUserDB(userUuid, project)
          if (project.items && project.items.length > 0) {
            const itemsWithProjectId = project.items.map((item: any) => ({
              ...item,
              projectId: project.id
            }))
            await saveItemsToUserDB(userUuid, itemsWithProjectId)
          }
        }
        
        set({ data: serverData })
        return
      }

      // 如果都没有数据，创建默认项目
      const defaultProject: Project = {
        id: uuidv4(),
        uuid: uuidv4(),
        name: '新画布',
        groups: [],
        items: [],
        canvasOffset: { x: 0, y: 0 },
        canvasScale: 1,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        opened: true // 新项目默认打开
      }
      const newData: AppData = {
        projects: [defaultProject],
        currentProjectId: defaultProject.id ?? null,
        userUuid
      }
      set({ data: newData })
      
      // 保存到用户数据库
      await saveProjectToUserDB(userUuid, defaultProject)
    } catch (error) {
      console.error('初始化数据失败:', error)
    }
  },

  setUserUuid: async (uuid: string) => {
    // 切换用户时，先保存当前用户数据
    const { userUuid: currentUuid } = get()
    if (currentUuid && currentUuid !== uuid) {
      // 保存当前用户数据到用户数据库
      await get().saveData()
    }
    
    // 切换到新用户
    await switchUser(uuid)
    
    // 更新 store 中的 userUuid
    set({ userUuid: uuid })
    
    // 清空当前数据，避免显示旧用户的数据
    set({ data: { ...defaultData, userUuid: uuid } })
    
    console.log('[setUserUuid] 开始加载新用户数据:', uuid)
    
    // 从用户数据库加载新用户的数据
    let newData: AppData = { ...defaultData, userUuid: uuid }
    
    try {
      // 加载所有项目
      const allProjects = await getProjectsFromUserDB(uuid)

      // 加载所有 items
      const allItems = await getAllItemsFromUserDB(uuid)

      // 将 items 分配到对应的项目
      const projectsWithItems = allProjects.map(project => ({
        ...project,
        items: allItems.filter(item => item.projectId === project.id || item.projectId === project.uuid) || [],
        groups: project.groups || []
      }))

      if (projectsWithItems.length > 0) {
        const openedProject = projectsWithItems.find(p => p.opened)
        newData = {
          projects: projectsWithItems,
          currentProjectId: openedProject?.id || projectsWithItems[0].id,
          userUuid: uuid
        }
        console.log('[setUserUuid] 从用户数据库加载了', projectsWithItems.length, '个项目')
      } else {
        // 尝试从服务器加载
        const serverData = await getUserData(uuid)
        if (serverData && serverData.projects && serverData.projects.length > 0) {
          newData = { ...serverData, userUuid: uuid }
          console.log('[setUserUuid] 从服务器加载了', serverData.projects.length, '个项目')
          
          // 保存到用户数据库
          for (const project of serverData.projects) {
            await saveProjectToUserDB(uuid, project)
            if (project.items && project.items.length > 0) {
              const itemsWithProjectId = project.items.map((item: any) => ({
                ...item,
                projectId: project.id
              }))
              await saveItemsToUserDB(uuid, itemsWithProjectId)
            }
          }
        } else {
          console.log('[setUserUuid] 新用户没有项目数据')
        }
      }
    } catch (error) {
      console.warn('[setUserUuid] 加载数据失败:', error)
    }
    
    // 更新 store
    set({ data: newData, userUuid: uuid })
    console.log('[setUserUuid] 数据已更新，项目数量:', newData.projects.length)
    
    // 获取最新的数据（用于后续操作）
    const latestData = get().data
    
    // 通知原生代码
    const win = window as any
    
    // 1. 首先清空画布 DOM（注意：实际的画布内容在 canvasContent 中）
    console.log('[setUserUuid] 清空原生代码画布')
    const canvasContent = document.getElementById('canvasContent')
    if (canvasContent) {
      canvasContent.innerHTML = ''
    }
    
    // 2. 同步数据到原生代码
    // 优先使用 syncDataFromReact 函数（更可靠）
    if (typeof win.syncDataFromReact === 'function') {
      win.syncDataFromReact(latestData)
      console.log('[setUserUuid] 通过 syncDataFromReact 同步数据，项目数:', latestData.projects.length)
    } else if (win.data && typeof win.data === 'object') {
      // 备选：直接设置原生代码的数据对象
      win.data.userUuid = uuid
      win.data.projects = latestData.projects || []
      win.data.currentProjectId = latestData.currentProjectId || (latestData.projects?.[0]?.id || null)
      console.log('[setUserUuid] 直接设置原生代码数据，项目数:', win.data.projects.length)
    } else {
      console.warn('[setUserUuid] 无法同步数据到原生代码：syncDataFromReact 和 win.data 都不可用')
    }
    
    // 3. 重新渲染项目标签和画布
    if (typeof win.renderProjectsTabs === 'function') {
      win.renderProjectsTabs()
    }
    if (typeof win.renderCanvas === 'function') {
      win.renderCanvas()
    }
    
    // 4. 初始化画布平移和小地图
    if (typeof win.initCanvasPan === 'function') {
      win.initCanvasPan()
    }
    if (typeof win.initMinimap === 'function') {
      win.initMinimap()
    }
    
    // 发送用户切换完成事件，通知所有组件刷新数据
    console.log('[setUserUuid] 发送 userSwitched 事件')
    window.dispatchEvent(new CustomEvent('userSwitched', { detail: { userUuid: uuid } }))
  },

  setCurrentProject: (projectId: string | null) => {
    const { userUuid } = get()
    
    set((state) => ({
      data: {
        ...state.data,
        currentProjectId: projectId
      }
    }))
    
    // 更新项目的 opened 状态
    if (userUuid && projectId) {
      updateProjectOpened(userUuid, projectId, true).catch(console.error)
    }
    
    get().saveData()
  },

  addProject: (project: Project) => {
    const { userUuid } = get()
    
    // 确保新项目有 opened 属性
    const projectWithOpened = {
      ...project,
      opened: true
    }
    
    set((state) => ({
      data: {
        ...state.data,
        projects: [...state.data.projects, projectWithOpened],
        currentProjectId: project.id ?? null
      }
    }))
    
    // 保存到用户数据库
    if (userUuid) {
      saveProjectToUserDB(userUuid, projectWithOpened).catch(console.error)
    }
    
    get().saveData()
  },

  updateProject: (projectId: string, updates: Partial<Project>, syncToServer: boolean = true) => {
    const { userUuid } = get()
    
    set((state) => ({
      data: {
        ...state.data,
        projects: state.data.projects.map((p) =>
          p.id === projectId ? { ...p, ...updates, updatedAt: Date.now() } : p
        )
      }
    }))
    
    // 保存到用户数据库
    if (userUuid) {
      const updatedProject = get().data.projects.find(p => p.id === projectId)
      if (updatedProject) {
        saveProjectToUserDB(userUuid, updatedProject).catch(console.error)
      }
    }
    
    // 根据参数决定是否同步到服务器
    get().saveData(syncToServer)
  },

  deleteProject: (projectId: string) => {
    const state = get()
    // 找到要删除的项目
    const deletedProject = state.data.projects.find((p) => p.id === projectId || p.uuid === projectId)
    
    if (!deletedProject) {
      console.warn(`项目不存在: ${projectId}`)
      return
    }
    
    // 同时删除服务器上的项目
    const { userUuid } = state
    if (userUuid && deletedProject.uuid) {
      deleteProjectApi(userUuid, deletedProject.uuid).catch((error) => {
        console.error('删除服务器项目失败:', error)
        // 即使服务器删除失败，本地删除仍然成功
      })
      
      // 从用户数据库删除
      if (deletedProject.id) {
        deleteProjectFromUserDB(userUuid, deletedProject.id).catch(console.error)
      }
    }
    
    // 同步删除 legacy-script 中的数据
    const win = window as any
    if (typeof win.deleteProject === 'function' && deletedProject.id) {
      try {
        win.deleteProject(deletedProject.id)
      } catch (error) {
        console.warn('同步删除 legacy-script 项目失败:', error)
      }
    }
    
    set((state) => {
      // 支持通过 id 或 uuid 删除
      const newProjects = state.data.projects.filter((p) => {
        return p.id !== projectId && p.uuid !== projectId
      })
      
      // 检查是否删除了当前项目
      const newCurrentProjectId =
        deletedProject && (state.data.currentProjectId === deletedProject.id || state.data.currentProjectId === deletedProject.uuid)
          ? newProjects.length > 0
            ? (newProjects[0].id || newProjects[0].uuid)
            : null
          : state.data.currentProjectId

      return {
        data: {
          ...state.data,
          projects: newProjects,
          currentProjectId: newCurrentProjectId
        }
      }
    })
    
    get().saveData()
  },

  saveData: async (syncToServer: boolean = true) => {
    const { data, userUuid } = get()
    if (!userUuid) return

    try {
      // 保存到用户数据库
      for (const project of data.projects) {
        // 保存项目（不包含 items）
        const projectToSave = { ...project }
        delete (projectToSave as any).items
        await saveProjectToUserDB(userUuid, projectToSave)
        
        // 保存 items
        if (project.items && project.items.length > 0) {
          const itemsWithProjectId = project.items.map(item => ({
            ...item,
            projectId: project.id
          }))
          await saveItemsToUserDB(userUuid, itemsWithProjectId)
        }
      }

      // 只有在需要时才保存到服务器（全量同步）
      if (syncToServer) {
        await saveUserData(userUuid, data)
      }
    } catch (error) {
      console.error('保存数据失败:', error)
    }
  },

  getCurrentProject: () => {
    const { data } = get()
    if (!data.currentProjectId) return null
    return data.projects.find((p) => p.id === data.currentProjectId) || null
  },

  // 从服务器拉取单个项目
  pullProjectFromServer: async (projectId: string) => {
    const { data, userUuid } = get()
    if (!userUuid) {
      throw new Error('用户未登录')
    }

    const project = data.projects.find((p) => p.id === projectId)
    if (!project) {
      throw new Error('项目不存在')
    }

    // 如果项目没有UUID，先生成UUID并上传
    if (!project.uuid) {
      project.uuid = uuidv4()
      // 确保所有items都有UUID
      if (project.items) {
        project.items.forEach((item) => {
          if (!item.uuid) {
            item.uuid = uuidv4()
          }
          if (!item.updatedAt) {
            item.updatedAt = new Date().toISOString()
          }
        })
      }
      if (!project.updatedAt) {
        project.updatedAt = new Date().toISOString()
      }
      // 先上传项目
      await pushProject(userUuid!, project)
      await get().saveData()
    }

    // 从服务器拉取项目
    const serverProject = await getProject(userUuid, project.uuid)
    if (!serverProject) {
      throw new Error('云端没有该项目数据')
    }

    // 合并项目数据（简单合并，保留本地数据）
    const mergedProject: Project = {
      ...project,
      ...serverProject,
      id: project.id, // 保留本地ID
      groups: (project.groups && project.groups.length > 0) ? project.groups : (serverProject.groups || []),
      items: (project.items && project.items.length > 0) ? project.items : (serverProject.items || []),
      coverImage: serverProject.coverImage || project.coverImage // 优先使用服务器返回的封面
    }

    // 更新项目
    set((state) => ({
      data: {
        ...state.data,
        projects: state.data.projects.map((p) =>
          p.id === projectId ? mergedProject : p
        )
      }
    }))

    await get().saveData()
  },

  // 推送单个项目到服务器（包含媒体文件上传）
  pushProjectToServer: async (projectId: string) => {
    const { data, userUuid } = get()
    if (!userUuid) {
      throw new Error('用户未登录')
    }

    const project = data.projects.find((p) => p.id === projectId)
    if (!project) {
      throw new Error('项目不存在')
    }

    // 如果项目没有UUID，生成UUID
    if (!project.uuid) {
      project.uuid = uuidv4()
    }

    // 使用新的存储服务推送项目（包含媒体上传）
    const result = await pushProjectWithMedia(userUuid, project)
    
    if (!result.success) {
      throw new Error(result.error || '上传失败')
    }

    // 更新本地数据
    set((state) => ({
      data: {
        ...state.data,
        projects: state.data.projects.map((p) =>
          p.id === projectId ? { ...p, ...project } : p
        )
      }
    }))

    // 只保存到本地 IndexedDB，不同步到服务器（因为我们已经单独推送了这个项目）
    await get().saveData(false)
  },

  // 清空项目数据
  clearProjectData: async (projectId: string, clearServer = false) => {
    const { data, userUuid } = get()
    const project = data.projects.find((p) => p.id === projectId)
    if (!project) {
      throw new Error('项目不存在')
    }

    // 如果选择清空服务器数据，先清空服务器上的项目数据
    if (clearServer && userUuid && project.uuid) {
      try {
        await deleteProjectApi(userUuid, project.uuid)
      } catch (error) {
        console.error('清空服务器项目数据失败:', error)
        throw error
      }
    }

    // 清空本地项目数据
    const clearedProject: Project = {
      ...project,
      groups: [],
      items: []
    }

    set((state) => ({
      data: {
        ...state.data,
        projects: state.data.projects.map((p) =>
          p.id === projectId ? clearedProject : p
        )
      }
    }))

    await get().saveData()

    // 如果清空了服务器数据，删除项目
    if (clearServer) {
      get().deleteProject(projectId)
    }
  },

  // 批量上传所有项目到服务器
  pushAllProjectsToServer: async (onProgress) => {
    const { data, userUuid, pushProjectToServer } = get()
    if (!userUuid) {
      throw new Error('用户未登录')
    }

    const projects = data.projects
    let success = 0
    let failed = 0

    for (let i = 0; i < projects.length; i++) {
      const project = projects[i]
      try {
        onProgress?.(i + 1, projects.length, project.name || '未命名画布')
        if (project.id) {
          await pushProjectToServer(project.id)
        }
        success++
      } catch (error) {
        console.error(`上传项目 ${project.name} 失败:`, error)
        failed++
      }
    }

    return { success, failed }
  },

  // 批量从服务器下载更新所有项目
  pullAllProjectsFromServer: async (onProgress) => {
    const { data, userUuid, saveData } = get()
    if (!userUuid) {
      throw new Error('用户未登录')
    }

    // 1. 先从服务器获取所有项目列表（包含完整的 items 数据）
    const serverData = await getUserData(userUuid)
    if (!serverData || !serverData.projects || serverData.projects.length === 0) {
      throw new Error('服务器上没有项目数据')
    }

    const serverProjects = serverData.projects
    const localProjects = data.projects
    let success = 0
    let failed = 0

    // 辅助函数：比较更新时间，返回服务器是否更新
    const isServerNewer = (localUpdatedAt: number | string | undefined, serverUpdatedAt: number | string | undefined): boolean => {
      if (!serverUpdatedAt) return false
      if (!localUpdatedAt) return true
      
      const localTime = typeof localUpdatedAt === 'string' ? new Date(localUpdatedAt).getTime() : localUpdatedAt
      const serverTime = typeof serverUpdatedAt === 'string' ? new Date(serverUpdatedAt).getTime() : serverUpdatedAt
      
      return serverTime > localTime
    }

    // 2. 遍历服务器项目列表，下载并更新本地项目
    const projectsToUpdate: Project[] = []
    const projectsToAdd: Project[] = []
    
    for (let i = 0; i < serverProjects.length; i++) {
      const serverProject = serverProjects[i]
      try {
        onProgress?.(i + 1, serverProjects.length, serverProject.name || '未命名画布')
        
        // 检查本地是否已有该项目（通过 uuid 或 id 匹配）
        const localProject = localProjects.find(
          p => (p.uuid && serverProject.uuid && p.uuid === serverProject.uuid) ||
               (p.id && serverProject.uuid && p.id === serverProject.uuid) ||
               (p.uuid && serverProject.id && p.uuid === serverProject.id) ||
               (p.id && serverProject.id && p.id === serverProject.id)
        )
        
        if (localProject) {
          // 如果本地已有，比较 updatedAt 决定是否更新
          if (isServerNewer(localProject.updatedAt, serverProject.updatedAt)) {
            // 服务器数据更新，用服务器数据完全替换本地数据
            // 注意：保留服务器的 updatedAt，不要覆盖
            const updatedProject: Project = {
              ...serverProject,
              id: localProject.id, // 保留本地 ID
              uuid: serverProject.uuid || localProject.uuid,
              opened: localProject.opened // 保留本地的打开状态
            }
            projectsToUpdate.push(updatedProject)
            console.log(`[pullAll] 项目 "${serverProject.name}" 将从服务器更新`)
          } else {
            console.log(`[pullAll] 项目 "${serverProject.name}" 本地已是最新，跳过`)
          }
        } else {
          // 如果本地没有，添加新项目
          const projectToAdd: Project = {
            ...serverProject,
            id: serverProject.uuid || serverProject.id || uuidv4(),
            uuid: serverProject.uuid || serverProject.id || uuidv4()
          }
          projectsToAdd.push(projectToAdd)
          console.log(`[pullAll] 新项目 "${serverProject.name}" 将被添加`)
        }
        
        success++
      } catch (error) {
        console.error(`下载项目 ${serverProject.name} 失败:`, error)
        failed++
      }
    }

    // 3. 批量更新状态（避免多次触发渲染）
    if (projectsToUpdate.length > 0 || projectsToAdd.length > 0) {
      set((state) => {
        let newProjects = [...state.data.projects]
        
        // 更新已有项目
        for (const updatedProject of projectsToUpdate) {
          const index = newProjects.findIndex(p => p.id === updatedProject.id)
          if (index !== -1) {
            newProjects[index] = updatedProject
          }
        }
        
        // 添加新项目
        newProjects = [...newProjects, ...projectsToAdd]
        
        return {
          data: {
            ...state.data,
            projects: newProjects
          }
        }
      })
      
      // 保存更新的项目到用户数据库
      for (const project of [...projectsToUpdate, ...projectsToAdd]) {
        await saveProjectToUserDB(userUuid, project)
      }
    }

    // 4. 保存数据到本地
    await saveData()

    // 5. 下载所有项目的媒体文件
    console.log('[pullAll] 开始下载媒体文件...')
    try {
      const { downloadMediaFilesFromServer } = await import('../services/mediaDownloader')
      const mediaResult = await downloadMediaFilesFromServer(userUuid, get().data.projects)
      console.log(`[pullAll] 媒体文件下载完成: 成功 ${mediaResult.success} 个, 失败 ${mediaResult.failed} 个, 跳过 ${mediaResult.skipped} 个`)
    } catch (error) {
      console.error('[pullAll] 下载媒体文件失败:', error)
      // 媒体下载失败不影响项目同步的成功状态
    }

    // 6. 本地有但服务器没有的项目会被保留（不做任何操作）

    return { success, failed }
  }
}))
